/**
 * WeeklyDigestSection Component
 *
 * Weekly digest settings section (without Card wrapper) with:
 * - Day and time configuration
 * - Content section toggles
 * - Preview digest button (text button)
 *
 * Designed to be used within NotificationScheduleCard for unified layout.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
  Select,
  Button,
} from '@shopify/polaris';
import { CalendarIcon, ViewIcon } from '@shopify/polaris-icons';
import { TIME_OPTIONS } from '../../../constants/time-options';
import { Toggle } from '../../ui';
import { AppBridgeModal } from '../../app-bridge/AppBridgeModal';
import { useModalRoute } from '../../../hooks/core/useModalRoute';

export interface WeeklyDigestSectionProps {
  /** Weekly digest day (0=Sunday, 6=Saturday) */
  weeklyDigestDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Weekly digest time (HH:MM format) */
  weeklyDigestTime: string;
  /** Handler for weekly digest day change */
  onWeeklyDigestDayChange: (day: number) => void;
  /** Handler for weekly digest time change */
  onWeeklyDigestTimeChange: (time: string) => void;
  /** Weekly digest sections */
  weeklyDigestSections: Array<string>;
  /** Handler for weekly digest sections change */
  onWeeklyDigestSectionsChange: (sections: Array<string>) => void;
  /** Shop domain for test digest feature */
  shopDomain?: string;
}

const DAY_OPTIONS = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
];

/** Inline toggle row for digest section options */
interface ToggleRowProps {
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function ToggleRow({ label, enabled, onChange }: ToggleRowProps) {
  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false}>
      <Text as="span" variant="bodyMd">
        {label}
      </Text>
      <Toggle
        label={label}
        enabled={enabled}
        onChange={onChange}
      />
    </InlineStack>
  );
}

export function WeeklyDigestSection({
  weeklyDigestDay,
  weeklyDigestTime,
  onWeeklyDigestDayChange,
  onWeeklyDigestTimeChange,
  weeklyDigestSections,
  onWeeklyDigestSectionsChange,
  shopDomain = '',
}: WeeklyDigestSectionProps) {
  const [searchParams] = useSearchParams();

  // Route-based modal for preview digest
  const previewDigestModal = useModalRoute('/app/settings/modal/preview-digest');

  // Handle opening preview modal
  const handleOpenPreview = useCallback(() => {
    const shop = searchParams.get('shop') || shopDomain;
    previewDigestModal.open({
      shop,
      sections: JSON.stringify(weeklyDigestSections),
      day: String(weeklyDigestDay),
      time: weeklyDigestTime,
    });
  }, [searchParams, shopDomain, weeklyDigestSections, weeklyDigestDay, weeklyDigestTime, previewDigestModal]);

  const handleClosePreview = useCallback(() => {
    previewDigestModal.close();
  }, [previewDigestModal]);

  return (
    <>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack gap="200" blockAlign="start" wrap={false}>
          <Box paddingBlockStart="050">
            <Icon source={CalendarIcon} tone="subdued" />
          </Box>
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">
              Weekly digest
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Summary sent to recipients with digest enabled
            </Text>
          </BlockStack>
        </InlineStack>

        {/* Content */}
        <BlockStack gap="300">
          {/* Schedule - Tinted background */}
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">
                Schedule
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="140px">
                  <Select
                    label="Day"
                    labelHidden
                    options={DAY_OPTIONS}
                    value={String(weeklyDigestDay)}
                    onChange={(value) => onWeeklyDigestDayChange(Number(value))}
                  />
                </Box>
                <Box minWidth="140px">
                  <Select
                    label="Time"
                    labelHidden
                    options={TIME_OPTIONS}
                    value={weeklyDigestTime}
                    onChange={onWeeklyDigestTimeChange}
                  />
                </Box>
              </InlineStack>
            </BlockStack>
          </Box>

          {/* Sections - Tinted background */}
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <BlockStack gap="300">
              <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">
                Sections
              </Text>
              <ToggleRow
                label="Weekly performance summary"
                enabled={weeklyDigestSections.includes('performance_summary')}
                onChange={(enabled) => {
                  const newSections = enabled
                    ? [...weeklyDigestSections, 'performance_summary']
                    : weeklyDigestSections.filter((s) => s !== 'performance_summary');
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Inventory health overview"
                enabled={weeklyDigestSections.includes('health_overview') || weeklyDigestSections.includes('alerts')}
                onChange={(enabled) => {
                  let newSections = weeklyDigestSections.filter((s) => s !== 'health_overview' && s !== 'alerts');
                  if (enabled) {
                    newSections = [...newSections, 'health_overview'];
                  }
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Products requiring attention"
                enabled={weeklyDigestSections.includes('attention_required') || weeklyDigestSections.includes('top_products')}
                onChange={(enabled) => {
                  let newSections = weeklyDigestSections.filter((s) => s !== 'attention_required' && s !== 'top_products');
                  if (enabled) {
                    newSections = [...newSections, 'attention_required'];
                  }
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Velocity trends"
                enabled={weeklyDigestSections.includes('velocity_trends')}
                onChange={(enabled) => {
                  const newSections = enabled
                    ? [...weeklyDigestSections, 'velocity_trends']
                    : weeklyDigestSections.filter((s) => s !== 'velocity_trends');
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
            </BlockStack>
          </Box>

          {/* Preview Button - Text button */}
          {shopDomain && (
            <Box>
              <Button
                icon={ViewIcon}
                onClick={handleOpenPreview}
              >
                Preview digest
              </Button>
            </Box>
          )}
        </BlockStack>
      </BlockStack>

      {/* Preview Digest Modal - Route-based */}
      {shopDomain && (
        <AppBridgeModal
          src={previewDigestModal.src}
          open={previewDigestModal.isOpen}
          onClose={handleClosePreview}
          variant="large"
        />
      )}
    </>
  );
}
